import { Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AssetPickerConfigDto } from '../../../../../../../api/src/creative-unit-package/models/customization/customization.dto';
import { AssetDto } from '../../../../../../../api/src/asset/models/asset.dto';
import { PublicDataSource } from '../../../../../../../api/src/data-source/data-source.entity';
import { ProjectsQuery } from '../../project';
import { BusinessQuery } from '../../business';
import { GlobalQuery, GlobalService } from '../../global';
import { environment } from '../../../../../environments/environment';
import { SessionQuery } from '../../session';
import { ObjectUtils } from '../../../../../../../api/src/_core/utils/utils.object';
import { GlobalSettings } from '../../global/global.model';
import { Observable } from 'rxjs';

const SIDEBAR_STATE_KEY = 'asset-picker-sidebar-collapsed';

@Component({
	selector: 'app-asset-picker-dialog',
	templateUrl: './asset-picker-dialog.component.html',
	styleUrls: ['./asset-picker-dialog.component.scss']
})
export class AssetPickerDialogComponent implements OnInit, OnDestroy {

	public config: AssetPickerConfigDto;
	public selectedItems: AssetDto[] = [];
	public state: 'asset-picker' | 'upload' = 'asset-picker';
	public selectionChanged: boolean = false;
	public activeDatasource: PublicDataSource | null = null;
	public isSidebarCollapsed: boolean = false;
	public isLoadingAssets: boolean = false;
	public settings$: Observable<GlobalSettings>;

	constructor(
		public dialogRef: MatDialogRef<AssetPickerDialogComponent>,
		public projectQuery: ProjectsQuery,
		public businessQuery: BusinessQuery,
		public globalService: GlobalService,
		private readonly globalQuery: GlobalQuery,
		public sessionQuery: SessionQuery,
		@Inject(MAT_DIALOG_DATA) public data: {
			config: AssetPickerConfigDto
		}
	) {
		// Load persisted sidebar state
		this.isSidebarCollapsed = localStorage.getItem(SIDEBAR_STATE_KEY) === 'true';
		this.settings$ = this.globalQuery.select('settings');
	}

	/**
	 * Only show sidebar when multiple enabled datasources exist (excluding errored sources)
	 */
	get shouldShowSidebar(): boolean {
		const enabledSources = this.data.config.datasources?.filter(
			source => (source as any).enabled !== false && !(source as any).authError
		) || [];
		return enabledSources.length > 1;
	}

	/**
	 * Get list of enabled datasources for display
	 */
	get enabledDatasources(): PublicDataSource[] {
		return this.data.config.datasources?.filter(
			source => (source as any).enabled !== false && !(source as any).authError
		) || [];
	}

	/**
	 * Legacy getter for backward compatibility
	 * @deprecated Use shouldShowSidebar instead
	 */
	get isSidebarMode(): boolean {
		return this.shouldShowSidebar;
	}


	/**
	 * Return to the asset picker from upload state
	 */
	returnToPicker(): void {
		this.setState('asset-picker');
	}

	ngOnInit(): void {
		const project = this.projectQuery.getActive();
		let business = project?.business ? project.business : this.businessQuery.getActive();

		const user = this.sessionQuery.getProfile();

		if (!business) {
			business = user.businesses[0];
		}

		if (this.data.config.selectedItems) {
			this.selectedItems = this.data.config.selectedItems;
		}

		let config = {
			...this.data.config
		};

		// Handle multi-datasource mode: set first enabled datasource as active
		if (this.shouldShowSidebar) {
			const enabledSources = this.enabledDatasources;
			this.activeDatasource = enabledSources[0];
			config.datasource = this.activeDatasource;
		} else if (this.enabledDatasources.length === 1) {
			// Single datasource mode - use the only available datasource
			this.activeDatasource = this.enabledDatasources[0];
			config.datasource = this.activeDatasource;
		}

		if (!config.datasource) {
			this.globalService.triggerErrorMessage(undefined, $localize `:Error Message@@assetPickerNoDataSourceProvidedErrorMessage:No data source provided`);
			this.dialogRef.close();
			return;
		}

		// Apply datasource-specific filters
		this.applyDatasourceFilters(config.datasource, config, project, business);

		// Ensure UI config is set with proper showUpload value
		config.ui = {
			...config.ui,
			showUpload: config.datasource.slug === 'uploads'
		};

		this.config = config;
	}

	ngOnDestroy(): void {
		// Cleanup if needed
	}

	/**
	 * Apply datasource-specific query parameter filters based on datasource slug
	 */
	private applyDatasourceFilters(
		datasource: PublicDataSource,
		config: AssetPickerConfigDto,
		project: any,
		business: any
	): void {
		if (datasource.slug === 'siteAssets') {
			config.queryParams = {
				...(config.queryParams || {}),
				filter: {
					...(config.queryParams?.filter || {}),
					siteId: environment.cmsSiteId
				}
			}
		}

		if (datasource.slug === 'businessAssets') {
			config.queryParams = {
				...(config.queryParams || {}),
				filter: {
					...(config.queryParams?.filter || {}),
					businessId: business.id
				}
			}
		}

		if (datasource.slug === 'projectAssets') {
			config.queryParams = {
				...(config.queryParams || {}),
				filter: {
					...(config.queryParams?.filter || {}),
					projectId: project.id
				}
			}
		}
	}

	selectItem(item: AssetDto) {
		if (!this.selectionChanged) {
			this.selectionChanged = true;
		}

		const index = this.selectedItems.findIndex(i => i.id === item.id);

		if (!this.data.config.selectMultiple) {
			if (index === -1) {
				this.selectedItems = [item];
			} else {
				this.selectedItems = [];
			}
		} else {
			if (index === -1) {
				this.selectedItems = [...this.selectedItems, item];
			} else {
				this.selectedItems = this.selectedItems.filter((_, i) => i !== index);
			}
		}
	}

	setState(state: 'asset-picker' | 'upload') {
		this.state = state;
	}

	skip() {
		this.dialogRef.close();
	}

	save() {
		this.dialogRef.close(this.selectedItems);
	}

	/**
	 * Select a new datasource and reload assets
	 */
	selectDatasource(datasource: PublicDataSource) {
		if (this.activeDatasource?.id === datasource.id) {
			return; // Already selected
		}

		this.isLoadingAssets = true;
		this.activeDatasource = datasource;

		const project = this.projectQuery.getActive();
		let business = project?.business ? project.business : this.businessQuery.getActive();
		const user = this.sessionQuery.getProfile();

		if (!business) {
			business = user.businesses[0];
		}

		// Create new config with selected datasource
		let newConfig = {
			...this.config,
			datasource: datasource,
			queryParams: {}, // Reset query params
			ui: {
				...this.config.ui,
				showUpload: datasource.slug === 'uploads' // Show upload button only for uploads datasource
			}
		};

		// Apply datasource-specific filters
		this.applyDatasourceFilters(datasource, newConfig, project, business);

		// Trigger change detection by creating new object reference
		this.config = { ...newConfig };

		// Loading state is controlled by the asset picker component
		this.isLoadingAssets = false;
	}

	/**
	 * Toggle sidebar collapsed state and persist to localStorage
	 */
	toggleSidebar() {
		this.isSidebarCollapsed = !this.isSidebarCollapsed;
		localStorage.setItem(SIDEBAR_STATE_KEY, this.isSidebarCollapsed.toString());
	}

	/**
	 * Handle keyboard navigation
	 */
	@HostListener('keydown', ['$event'])
	handleKeyboardEvent(event: KeyboardEvent) {
		// Navigate sidebar with arrow keys when focused
		if (this.shouldShowSidebar && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
			const activeElement = document.activeElement;
			if (activeElement?.closest('.datasource-sidebar')) {
				event.preventDefault();
				this.navigateSidebarItems(event.key === 'ArrowDown' ? 1 : -1);
			}
		}
	}

	/**
	 * Navigate through sidebar items
	 */
	private navigateSidebarItems(direction: 1 | -1) {
		const items = this.enabledDatasources;
		const currentIndex = items.findIndex(d => d.id === this.activeDatasource?.id);
		const newIndex = (currentIndex + direction + items.length) % items.length;
		this.selectDatasource(items[newIndex]);
	}

	getLocalizedName(item) {
		return item.locale?.name ? ObjectUtils.getLocale(item.name, item.locale.name) : item.name
	}
}
